<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useUsersStore } from '@/stores/users'
import { useAuthStore } from '@/stores/auth'
import checkValid from '@/helpers/checkValid'

const props = defineProps({
    uuid: {
        type: String
    }
})

const usersStore        = useUsersStore()
const translaterStore   = useTranslaterStore()
const authStore         = useAuthStore()

const getTranslate          = computed(() => translaterStore.getTranslate)
const userUpdateMsg         = computed(() => usersStore.userUpdateMsg)
const endUserSessionsMsg    = computed(() => authStore.endUserSessionsMsg)

const UTAUserPwdPassword        = ref('')
const UTAUserPwdPasswordRepeat  = ref('')
const showPass                  = ref(false)
const showPassRepeat            = ref(false)
const isSessionsEnd             = ref(false)
const formInvalids              = ref({})
const formErrors                = ref([])

async function submitChangePass() {

    formInvalids.value = {}
    formErrors.value = []

    const data = {

        uuid: props.uuid
    }

    if(UTAUserPwdPassword.value) {

        data.password = UTAUserPwdPassword.value

    } else {

        formInvalids.value.UTAUserPwdPassword = true
        formErrors.value.push(getTranslate.value.USER_PASS)
    }
    
    if(!UTAUserPwdPasswordRepeat.value || UTAUserPwdPassword.value !== UTAUserPwdPasswordRepeat.value) {

        formInvalids.value.UTAUserPwdPasswordRepeat = true
        formErrors.value.push(getTranslate.value.USER_PASS_REPEAT)
    }

    if(formErrors.value.length == 0) {
        
        await usersStore.updateUser(data)

        if(userUpdateMsg.value.success && isSessionsEnd.value) {
            //Завершить все сеансы
            await authStore.endUserSessions(props.uuid)

            //Сбросить значения
            isSessionsEnd.value = false

            UTAUserPwdPassword.value = ''
            UTAUserPwdPasswordRepeat.value = ''
        }

        if(endUserSessionsMsg.value.success && localStorage.getItem('UTA_UUID') == props.uuid) {
            //Очистить LocalStorage у текущего пользователя
            localStorage.clear()
        }
    }
}

onBeforeUnmount(() => {
    usersStore.setUpdateUserMsg({})
})
</script>

<template>

    <form class="mb-5 uta-user-pwd" @submit.prevent="submitForm($event)">

        <header class="uta-page-header" role="heading" aria-level="1">
            <h1 class="uta-page-header__title">{{ getTranslate.USER_PASS }}</h1>
        </header>

        <div class="page__content">

            <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate.EFormFields }}:
                    <span class="alert__text-item" v-for="(error, index) in formErrors" :key="error">
                        {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                    </span>
                </div>
                <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                    <span class="material-icons">close</span>
                </button>
            </div>

            <div 
                v-if="'success' in userUpdateMsg"
                class="mb-4 uta-alert" 
                :class="{
                    'uta-alert--success' : userUpdateMsg.success,
                    'uta-alert--danger' : !userUpdateMsg.success
                }"
            >
                <div class="material-icons uta-alert__icon">
                    {{ userUpdateMsg.success ? 'check_circle' : 'error_outline' }}
                </div>
                <div class="uta-alert__text">
                    {{ getTranslate[userUpdateMsg.text] }}
                </div>
                <button type="button" class="uta-alert__close" @click="usersStore.setUpdateUserMsg({})">
                    <span class="material-icons">close</span>
                </button>
            </div>

            <form action="" @submit.prevent="submitChangePass">
                <div class="row">
                    <div class="col-12 col-lg-6">
                        <div class="mb-4 form-group">
                            <label for="UTAUserPwdPassword" class="uta-user-pwd__field-label">{{ getTranslate.USER_PASS }}:</label>
                            <div class="uta-input__wrapper">
                                <input 
                                    :type="showPass ? 'text' : 'password'" 
                                    autocomplete="off"
                                    class="uta-input uta-input--block uta-input-wrapper__input"
                                    :class="{ 'uta-input--is-invalid' : formInvalids.UTAUserPwdPassword }" 
                                    id="UTAUserPassOld"
                                    v-model="UTAUserPwdPassword"
                                    @change="checkValid()"
                                >
                                <span class="material-icons uta-input__wrapper-icon" @click="showPass = !showPass">
                                    {{ showPass ? 'visibility' : 'visibility_off' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-lg-6">
                        <div class="mb-4 form-group">
                            <label for="UTAUserPwdPasswordRepeat" class="uta-user-pwd__field-label">{{ getTranslate.USER_PASS_REPEAT }}:</label>
                            <div class="uta-input__wrapper">
                                <input 
                                    :type="showPassRepeat ? 'text' : 'password'" 
                                    autocomplete="off"
                                    class="uta-input uta-input--block uta-input-wrapper__input" 
                                    :class="{ 'is-invalid' : formInvalids.UTAUserPwdPasswordRepeat }" 
                                    id="UTAUserPassNew"
                                    v-model="UTAUserPwdPasswordRepeat"
                                >
                                <span class="material-icons uta-input__wrapper-icon" @click="showPassRepeat = !showPassRepeat">
                                    {{ showPassRepeat ? 'visibility' : 'visibility_off' }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-lg-6">
                        <div class="mb-4 form-check">
                            <input 
                                type="checkbox" 
                                name="end-sessions"
                                class="form-check-input"
                                id="end-sessions"
                                v-model="isSessionsEnd"
                            > 
                            <label 
                                class="form-check-label" 
                                for="end-sessions"
                            >
                                {{ getTranslate.RESET_TOKEN }}
                            </label>
                        </div>
                    </div>
                </div>
                <div class="uta-btns">
                    <button type="submit" class="uta-btn uta-btn--main">{{ getTranslate.BTN_SAVE }}</button>
                </div>
            </form>

        </div>

    </form>

</template>